import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  fetchVehicleServiceHistory,
  VehicleServiceHistorySummary,
  VehicleServiceVisit,
} from "../../services/vehicleServiceHistoryApi";
import { statusBadgeClass } from "./jobCardActivityModuleConfig";
import {
  ServiceHistoryIcon,
  SortTimeAscIcon,
  SortTimeDescIcon,
} from "./HistoryIcons";

const TIMELINE_GUTTER_PX = 40;
const TIMELINE_LINE_LEFT = TIMELINE_GUTTER_PX / 2;

export interface VehicleServiceHistoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  vehicleNo?: string | null;
  vinNo?: string | null;
  rowSnapshot?: Record<string, any> | null;
  /** drawer = standalone portal; embedded = panel inside parent drawer */
  variant?: "drawer" | "embedded";
  /** For embedded: whether this tab is currently visible */
  isActive?: boolean;
}

function visitTimeMs(visit: VehicleServiceVisit): number {
  const raw = visit.date_in || "";
  if (!raw) return 0;
  const d = new Date(raw.includes("T") ? raw : raw.replace(" ", "T"));
  const t = d.getTime();
  return Number.isNaN(t) ? 0 : t;
}

function formatDateTime(iso: string): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso.includes("T") ? iso : iso.replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatInvoiceAmount(amount: number | null | undefined): string {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(Number(amount));
  } catch {
    return `₹${Number(amount).toLocaleString()}`;
  }
}

function summaryFromRow(row: Record<string, any> | null | undefined): VehicleServiceHistorySummary {
  return {
    customer_name: String(row?.customername ?? row?.customer_name ?? "—"),
    vehicle_no: String(
      row?.platenumber ?? row?.plate_number ?? row?.reg_no ?? row?.vehicle_no ?? "—"
    ),
    vin_no: String(row?.vin_no ?? row?.vin_number ?? row?.chassis_no ?? "—"),
    total_visits: 0,
  };
}

function displayOrDash(value: string | null | undefined): string {
  const s = (value ?? "").trim();
  return s || "—";
}

const MetaRow: React.FC<{
  label: string;
  value: React.ReactNode;
  className?: string;
  wrap?: boolean;
}> = ({ label, value, className = "", wrap = false }) => (
  <div className={`flex items-baseline gap-1.5 min-w-0 text-[11px] leading-snug ${className}`}>
    <span className="shrink-0 text-slate-400 dark:text-gray-500">{label}</span>
    <span
      className={`min-w-0 text-slate-700 dark:text-gray-300 ${
        wrap ? "break-words [overflow-wrap:anywhere]" : "truncate"
      }`}
    >
      {value}
    </span>
  </div>
);

const VisitCard: React.FC<{
  visit: VehicleServiceVisit;
  expanded: boolean;
  onToggle: () => void;
}> = ({ visit, expanded, onToggle }) => {
  const serviceLine = [
    visit.service_type || null,
    visit.visit_number ? `Visit #${visit.visit_number}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="relative flex gap-2 mb-3">
      <div
        className="relative z-10 flex shrink-0 justify-center pt-1.5"
        style={{ width: TIMELINE_GUTTER_PX }}
      >
        <div className="w-2.5 h-2.5 rounded-full bg-primary-500/90 ring-[3px] ring-slate-50 dark:ring-gray-900" />
      </div>
      <div className="flex-1 min-w-0 rounded-lg border border-slate-200/70 bg-white/55 dark:bg-gray-800/45 dark:border-gray-700/60 px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex items-baseline gap-2">
            <span className="text-sm font-semibold text-slate-800 dark:text-gray-200 truncate">
              {displayOrDash(visit.jobcard_no)}
            </span>
            {serviceLine ? (
              <span className="text-[10px] text-slate-400 dark:text-gray-500 truncate">
                {serviceLine}
              </span>
            ) : null}
          </div>
          {visit.status ? (
            <span
              className={`shrink-0 px-1.5 py-px rounded text-[9px] font-medium uppercase tracking-wide opacity-90 ${statusBadgeClass(
                visit.status
              )}`}
            >
              {visit.status}
            </span>
          ) : null}
        </div>

        {visit.vin_no ? (
          <div className="mt-1 text-[10px] text-slate-500 dark:text-gray-400 truncate">
            VIN: {visit.vin_no}
          </div>
        ) : null}

        <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5">
          <MetaRow label="In" value={formatDateTime(visit.date_in)} />
          <MetaRow label="Out" value={formatDateTime(visit.date_out)} />
          <MetaRow label="Salesman" value={displayOrDash(visit.salesman)} />
          <MetaRow label="Receiver" value={displayOrDash(visit.receiver_name)} />
          <MetaRow
            label="Invoice"
            value={
              <span className="font-medium text-slate-800 dark:text-gray-200">
                {formatInvoiceAmount(visit.invoice_amount)}
              </span>
            }
          />
        </div>

        <MetaRow
          className="mt-1"
          label="Complaint"
          wrap
          value={
            <span className="whitespace-pre-wrap">
              {displayOrDash(visit.customer_complaint)}
            </span>
          }
        />

        {expanded ? (
          <div className="mt-2 pt-2 border-t border-dashed border-slate-200/80 dark:border-gray-600/70 space-y-2">
            <div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-[10px] font-medium text-slate-500 dark:text-gray-400">
                  Service Progress
                </span>
                {visit.status ? (
                  <span className="text-[9px] font-medium text-emerald-700/80 dark:text-emerald-300/80">
                    {visit.status}
                  </span>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-1">
                {(visit.service_progress || []).length > 0 ? (
                  visit.service_progress.map((step) => (
                    <span
                      key={step}
                      className="inline-flex items-center gap-0.5 px-1.5 py-px rounded bg-emerald-50/80 text-emerald-800/90 dark:bg-emerald-900/25 dark:text-emerald-200/90 text-[9px] font-medium"
                    >
                      <span aria-hidden>✓</span>
                      {step}
                    </span>
                  ))
                ) : (
                  <span className="text-[11px] text-slate-400">—</span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
              <MetaRow
                label="Parts"
                value={
                  visit.parts_issued_count > 0
                    ? `${visit.parts_issued_count} Issues`
                    : "—"
                }
              />
              <MetaRow
                label="QC"
                value={[visit.qc_status, visit.qc_ref].filter(Boolean).join(" · ") || "—"}
              />
              <MetaRow
                label="Payment"
                value={displayOrDash(visit.payment_status || undefined)}
              />
              <MetaRow label="Gate Pass" value={displayOrDash(visit.gate_pass_no)} />
              <MetaRow
                className="col-span-2"
                label="Recv. Mobile"
                value={displayOrDash(visit.receiver_mobile)}
              />
            </div>
          </div>
        ) : null}

        <div className="mt-1.5 flex justify-end">
          <button
            type="button"
            onClick={onToggle}
            className="text-[11px] font-medium text-primary-700/90 dark:text-primary-300/90 hover:underline inline-flex items-center gap-0.5"
          >
            {expanded ? (
              <>
                Hide Details
                <span aria-hidden className="text-[9px]">
                  ▲
                </span>
              </>
            ) : (
              <>
                View Details
                <span aria-hidden>→</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export const VehicleServiceHistoryDrawer: React.FC<VehicleServiceHistoryDrawerProps> = ({
  isOpen,
  onClose,
  vehicleNo,
  vinNo,
  rowSnapshot,
  variant = "drawer",
  isActive = true,
}) => {
  const embedded = variant === "embedded";
  const [summary, setSummary] = useState<VehicleServiceHistorySummary | null>(null);
  const [visits, setVisits] = useState<VehicleServiceVisit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** false = newest first (BRD default Desc) */
  const [sortAsc, setSortAsc] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});
  const loadedVehicleKeyRef = React.useRef<string | null>(null);

  const instantSummary = useMemo(() => summaryFromRow(rowSnapshot), [rowSnapshot]);
  const hasKey = Boolean(
    (vehicleNo && String(vehicleNo).trim()) || (vinNo && String(vinNo).trim())
  );
  const vehicleLoadKey = `${vehicleNo || ""}|${vinNo || ""}`;

  const sortedVisits = useMemo(() => {
    const copy = [...visits];
    copy.sort((a, b) => {
      const diff = visitTimeMs(a) - visitTimeMs(b);
      if (diff !== 0) return sortAsc ? diff : -diff;
      const idA = String(a.jobcard_id || a.jobcard_no || "");
      const idB = String(b.jobcard_id || b.jobcard_no || "");
      return sortAsc ? idA.localeCompare(idB) : idB.localeCompare(idA);
    });
    return copy;
  }, [visits, sortAsc]);

  const load = useCallback(async () => {
    if (!hasKey) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchVehicleServiceHistory({
        vehicle_no: vehicleNo,
        vin_no: vinNo,
      });
      if (res?.success === false) {
        setError(res.message || "Could not load service history.");
        setVisits([]);
        setSummary(instantSummary);
        return;
      }
      setSummary(res.summary ?? instantSummary);
      setVisits(Array.isArray(res.visits) ? res.visits : []);
      setExpandedIds({});
      loadedVehicleKeyRef.current = vehicleLoadKey;
    } catch (e: any) {
      setError(e?.message || "Failed to load service history.");
      setSummary(instantSummary);
      setVisits([]);
    } finally {
      setLoading(false);
    }
  }, [hasKey, vehicleNo, vinNo, instantSummary, vehicleLoadKey]);

  useEffect(() => {
    if (!isOpen) {
      loadedVehicleKeyRef.current = null;
      return;
    }
    if (embedded && !isActive) return;
    if (!hasKey) {
      setVisits([]);
      setSummary(instantSummary);
      setError(null);
      return;
    }
    if (embedded && loadedVehicleKeyRef.current === vehicleLoadKey) return;
    setVisits([]);
    setSummary(instantSummary);
    setSortAsc(false);
    setExpandedIds({});
    void load();
  }, [isOpen, isActive, vehicleNo, vinNo, hasKey, embedded, vehicleLoadKey]);

  useEffect(() => {
    if (!isOpen || embedded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose, embedded]);

  if (!isOpen) return null;
  if (!embedded && !hasKey) return null;

  const displaySummary = summary ?? instantSummary;
  const vehicleLabel =
    displaySummary.vehicle_no && displaySummary.vehicle_no !== "—"
      ? displaySummary.vehicle_no
      : vehicleNo || vinNo || "—";

  const content = (
    <div className="h-full overflow-y-auto p-5 custom-scrollbar">
      {!hasKey ? (
        <p className="text-sm text-slate-500 dark:text-gray-400 py-8 text-center">
          No vehicle number available for this row.
        </p>
      ) : (
        <>
          <div className="rounded-xl border border-black/5 bg-gradient-to-br from-white to-slate-100 dark:from-gray-800 dark:to-gray-900 p-4 text-sm leading-relaxed mb-5 shadow-sm">
            <p>
              <span className="font-semibold">Customer:</span>{" "}
              {displayOrDash(displaySummary.customer_name)}
            </p>
            <p>
              <span className="font-semibold">Vehicle / VIN:</span>{" "}
              {displayOrDash(displaySummary.vehicle_no)}
              {displaySummary.vin_no && displaySummary.vin_no !== "—"
                ? ` / ${displaySummary.vin_no}`
                : ""}
            </p>
            <p>
              <span className="font-semibold">Total Visits:</span>{" "}
              {typeof displaySummary.total_visits === "number"
                ? `${displaySummary.total_visits} Visit${
                    displaySummary.total_visits === 1 ? "" : "s"
                  }`
                : "—"}
            </p>
          </div>

          <div className="flex items-center justify-between gap-2 mb-3">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-gray-200 m-0">
              Service Visits ({sortAsc ? "Oldest → Newest" : "Newest → Oldest"})
            </h3>
            <button
              type="button"
              onClick={() => setSortAsc((v) => !v)}
              title={
                sortAsc
                  ? "Showing oldest first — click for newest first"
                  : "Showing newest first — click for oldest first"
              }
              aria-label={sortAsc ? "Sort by date descending" : "Sort by date ascending"}
              className="inline-flex items-center gap-1 shrink-0 px-2 py-1 rounded-md border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800 hover:text-primary-700 dark:hover:text-primary-300 transition-colors text-xs font-medium"
            >
              {sortAsc ? (
                <SortTimeAscIcon className="w-4 h-4" />
              ) : (
                <SortTimeDescIcon className="w-4 h-4" />
              )}
              <span>{sortAsc ? "Asc" : "Desc"}</span>
            </button>
          </div>

          {error ? (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-800 dark:text-red-200 flex items-center justify-between gap-2">
              <span>{error}</span>
              <button
                type="button"
                className="text-xs font-semibold underline shrink-0"
                onClick={() => {
                  loadedVehicleKeyRef.current = null;
                  void load();
                }}
              >
                Retry
              </button>
            </div>
          ) : null}

          {loading ? (
            <div className="flex items-center justify-center py-12 text-sm text-slate-500">
              <span className="inline-block h-6 w-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mr-2" />
              Loading service history…
            </div>
          ) : visits.length === 0 && !error ? (
            <p className="text-sm text-slate-500 dark:text-gray-400 py-8 text-center">
              No service history found for this vehicle.
            </p>
          ) : (
            <div className="relative">
              <div
                className="absolute top-1 bottom-1 w-[3px] rounded-full pointer-events-none"
                style={{
                  left: TIMELINE_LINE_LEFT,
                  transform: "translateX(-50%)",
                  background:
                    "linear-gradient(to bottom, var(--color-primary-600, #2ea04a), var(--color-primary-200, #bbf7d0))",
                }}
                aria-hidden
              />
              {sortedVisits.map((visit) => {
                const key = visit.jobcard_id || visit.jobcard_no;
                return (
                  <VisitCard
                    key={key}
                    visit={visit}
                    expanded={Boolean(expandedIds[key])}
                    onToggle={() =>
                      setExpandedIds((prev) => ({
                        ...prev,
                        [key]: !prev[key],
                      }))
                    }
                  />
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );

  if (embedded) {
    return content;
  }

  const body = (
    <>
      <div
        className="fixed inset-0 z-[11999] bg-black/50 backdrop-blur-sm"
        aria-hidden
        onClick={onClose}
      />
      <div
        className="fixed top-0 right-0 z-[12000] h-full w-full max-w-[560px] bg-slate-50 dark:bg-gray-900 shadow-2xl flex flex-col border-l border-gray-200 dark:border-gray-700 transform transition-transform duration-300"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vehicle-service-history-drawer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-5 py-5 text-white bg-gradient-to-br from-primary-800 to-primary-500">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2
                id="vehicle-service-history-drawer-title"
                className="text-base font-bold m-0 flex items-center gap-2"
              >
                <ServiceHistoryIcon className="w-4 h-4 opacity-90" />
                Vehicle Service History
              </h2>
              <p className="text-xs opacity-90 mt-0.5">Vehicle: {vehicleLabel}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-white/90 hover:text-white text-2xl leading-none px-1"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden">{content}</div>
      </div>
    </>
  );

  if (typeof document === "undefined") return null;
  return createPortal(body, document.body);
};

export default VehicleServiceHistoryDrawer;
